import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "./styles/globals.css";
import App from "./App";

// Apply saved text-size preference before first paint so there's no flash.
const TEXT_SIZE_KEY = "text_size_pref";
const TEXT_SIZE_PX: Record<string, number> = { default: 16, large: 18, largest: 20 };
const savedSize = localStorage.getItem(TEXT_SIZE_KEY) ?? "default";
document.documentElement.style.fontSize = `${TEXT_SIZE_PX[savedSize] ?? 16}px`;

// Force the html element to exactly fill the visible screen.
// In iOS standalone (PWA) mode, window.innerHeight excludes the status bar
// but the viewport-fit:cover viewport covers the full physical screen, so we
// must use screen.height to avoid a gap below the bottom nav.
// In a regular browser, window.innerHeight is the correct value.
function setAppHeight() {
  const isStandalone =
    (window.navigator as unknown as { standalone?: boolean }).standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches;
  const h = isStandalone ? window.screen.height : window.innerHeight;
  document.documentElement.style.height = `${h}px`;
  document.documentElement.style.setProperty("--app-height", `${h}px`);
}
setAppHeight();
window.addEventListener("resize", setAppHeight);
window.addEventListener("orientationchange", () => setTimeout(setAppHeight, 100));

// Register service worker for PWA installability
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // SW registration failure is non-fatal
    });
  });
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element not found");

createRoot(rootEl).render(
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>
);
